import {
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Card } from "@magnetic/card";
import { CopySimple } from "@magnetic/icons";
import { Text } from "@magnetic/text";
import { Tooltip } from "@magnetic/tooltip";
import Papa from "papaparse";

import { Button } from "src/components/buttons";
import { EmptyContainer } from "src/components/emptyContainer";
import { HFlex } from "src/components/hFlex";
import { PageLoader } from "src/components/pageLoader";
import { StringColumn } from "src/components/tables/columnConfigs/stringColumn";
import TypedTable from "src/components/typedTable";
import { VFlex } from "src/components/vFlex";
import { TimeZoneContext } from "src/core/timeZoneProvider";
import { FORMAT } from "src/dataTypes/dateFormats";
import {
  CliCommandResponse,
  CommandAction,
  ResultFormat
} from "src/gen/schema/bilge/api";

import { copyMockshellCmds } from "./mockShellUtils";

import "./mockShellOutput.scss";
import "./mockShellCommandOutput.scss";

interface CommandHeaderProps {
  readonly command?: string;
  readonly executedAt?: Date;
}

export const CommandHeader = ({ command, executedAt }: CommandHeaderProps) => {
  const { getFormattedDate } = useContext(TimeZoneContext);
  if (!command) {
    return null;
  }
  return (
    <VFlex
      gap="xs"
      className="mock-shell-command-text"
      data-qa="mock-shell-command"
    >
      <Text weight="semi-bold">Command: {command}</Text>
      {executedAt && (
        <Text size="p5" color="light">
          Executed at: {getFormattedDate(executedAt, FORMAT.LONG)}
        </Text>
      )}
    </VFlex>
  );
};

interface MockShellOutputProps {
  readonly loading?: boolean;
  readonly cmdResult?: CliCommandResponse;
  readonly cmdAction?: CommandAction;
  readonly previousCommand?: string;
  readonly executedAt?: Date;
}

interface CsvData {
  [key: string]: string;
}
interface MockShellOutputCopyProps {
  readonly elementRef: RefObject<HTMLDivElement> | null;
}
// Magnetic's CopyToClipboard component expects only string as value for copying and does not maintains the formatting.
const MockShellOutputCopy = ({ elementRef }: MockShellOutputCopyProps) => {
  return (
    <Tooltip title="Copy output to clipboard">
      <Button
        name="mock-shell-copy"
        className="copy-button"
        kind="tertiary"
        size="sm"
        icon={<CopySimple />}
        onClick={() => copyMockshellCmds(elementRef)}
      >
        Copy
      </Button>
    </Tooltip>
  );
};

const MockShellCsv = (props: MockShellOutputProps) => {
  const { cmdResult, previousCommand } = props;
  const [tableData, setTableData] = useState<CsvData[]>([]);
  const [tableColumn, setTableColumn] =
    useState<Record<string, StringColumn>>();
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cmdResult?.executionResults) {
      const resultCSV = cmdResult.executionResults;
      Papa.parse<CsvData>(resultCSV, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.data && result.data.length > 0) {
            const columns = Object.keys(result.data[0] as CsvData).reduce(
              (acc: Record<string, StringColumn>, key) => {
                acc[key] = new StringColumn({
                  label: [key]
                });
                return acc;
              },
              {}
            );
            setTableColumn(columns);
            setTableData(result.data);
          }
        }
      });
    }
  }, [cmdResult?.executionResults]);

  if (!cmdResult?.executionResults || tableData.length === 0) {
    return (
      <VFlex data-qa="mock-shell-table-empty">
        <CommandHeader
          command={previousCommand}
          executedAt={props.executedAt}
        />
        <EmptyContainer text="No data found" />
      </VFlex>
    );
  }
  return (
    <VFlex data-qa="mock-shell-table">
      <HFlex justify="space-between" align="center">
        <CommandHeader
          command={previousCommand}
          executedAt={props.executedAt}
        />
        <div className="mock-shell-copy">
          <MockShellOutputCopy elementRef={divRef} />
        </div>
      </HFlex>
      <div ref={divRef} className="mock-shell-output-table-container">
        {tableColumn && (
          <TypedTable
            stickyHeader={false}
            name="mock-shell-out-table"
            columns={tableColumn}
            data={tableData}
          />
        )}
      </div>
    </VFlex>
  );
};

const MockShellText = (props: MockShellOutputProps) => {
  const { cmdResult, previousCommand } = props;
  const [formattedText, setFormattedText] = useState<string>("");
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cmdResult?.executionResults) {
      const result = cmdResult.executionResults;
      setFormattedText(result);
    }
  }, [cmdResult?.executionResults]);
  if (!cmdResult?.executionResults) {
    return (
      <VFlex data-qa="mock-shell-text-empty">
        <CommandHeader
          command={previousCommand}
          executedAt={props.executedAt}
        />
        <EmptyContainer text="No data found" />
      </VFlex>
    );
  }
  return (
    <VFlex data-qa="mock-shell-text">
      <HFlex justify="space-between" align="center">
        <CommandHeader
          command={previousCommand}
          executedAt={props.executedAt}
        />
        <div className="mock-shell-copy">
          <MockShellOutputCopy elementRef={divRef} />
        </div>
      </HFlex>
      <div ref={divRef} className="mock-shell-output-text-container">
        <pre className="mock-shell-output-text">{formattedText}</pre>
      </div>
    </VFlex>
  );
};

const MockShellHelpText = (props: MockShellOutputProps) => {
  const { cmdResult, cmdAction } = props;
  const formattedHelpText = () => {
    let helpResult: ReactNode[] = [];
    if (cmdResult?.commandCompletions) {
      helpResult = cmdResult?.commandCompletions.map((cmd) => {
        return (
          <div key={`${cmd.keyword}`} className="mock-shell-output-help">
            <div className="mock-shell-output-help-key">{cmd.keyword}</div>
            <div className="mock-shell-output-help">{cmd.helpString}</div>
          </div>
        );
      });
    }
    if (cmdResult?.validCommand) {
      helpResult.unshift(
        <div key="enter" className="mock-shell-output-help">
          <div className="mock-shell-output-help-key">{"<enter>"}</div>
          {!(cmdAction === CommandAction.EXPAND) && (
            <div className="mock-shell-output-help">
              {"Press enter to execute the command"}
            </div>
          )}
        </div>
      );
    }
    return helpResult;
  };
  return (
    <Card className="mock-shell-output-help-container">
      {formattedHelpText()}
    </Card>
  );
};

const DefaultResult = () => {
  return "";
};

export const MockShellOutput = (props: MockShellOutputProps) => {
  const { cmdResult, cmdAction, previousCommand, loading, executedAt } = props;
  const MockShellOutputComponent = useMemo(() => {
    if (
      cmdAction === CommandAction.COMPLETE &&
      (cmdResult?.commandCompletions?.length || 0) <= 1
    ) {
      return DefaultResult;
    }
    if (
      (cmdResult?.commandCompletions &&
        cmdResult?.commandCompletions.length > 0) ||
      cmdResult?.validCommand
    ) {
      return MockShellHelpText;
    }
    switch (cmdResult?.resultFormat) {
      // Backend needs to fix the resultFormat type returned for Text format here
      case ResultFormat.TEXT:
        return MockShellText;
      case ResultFormat.CSV:
        return MockShellCsv;
      default:
        return DefaultResult;
    }
  }, [cmdResult, cmdAction]);
  if (loading) {
    return (
      <VFlex
        align="center"
        justify="center"
        className="mock-shell-output-loader"
      >
        <PageLoader />
      </VFlex>
    );
  }
  return (
    <>
      {cmdResult ? (
        <MockShellOutputComponent
          cmdResult={cmdResult}
          cmdAction={cmdAction}
          previousCommand={previousCommand}
          executedAt={executedAt}
        />
      ) : (
        ""
      )}
    </>
  );
};
